import { oc } from "@orpc/contract";
import { z } from "zod";
import { RUNNER_PINS } from "../catalog-sidecar.js";
import { MEMORY_SCOPE_SLUG, MEMORY_SCOPES_PER_SESSION_MAX } from "../memory.js";
import { SessionViewSchema } from "../run-view.js";
import { HistoryRunSchema, RunCommandSchema, RunDetailSchema, RunEventEnvelopeSchema, RunSummarySchema, RunThreadSchema, RunThreadStatusSchema, } from "../wire.js";
import { RunPreflightSchema } from "./preflight.js";
// Runs: the MultiAgent-YAML session semantics in-process (workflows/V5-Plan.v2.md §4) —
// start composes a session from the project's current revision on the engine the model
// id selects, commands become the session's user events, the event log is the record.
// The live stream is GET /api/v5/runs/:id/events (SSE, Last-Event-ID), beside these.
/** What starting a run names: the project, an optional mission, and the cap (also the dev tools' preflight input). */
export const RunStartInputSchema = z.object({
    projectId: z.string().uuid(),
    /**
     * The project revision the caller expects to run (the one it showed or just saved). Given and
     * not the project's current revision, start is 409 draft_conflict {currentRevision}, as a
     * draft save is, and starts nothing; a preflight reports it as its first blocker. Absent, the
     * start runs whatever revision is current.
     */
    baseRevision: z.number().int().positive().optional(),
    mission: z.string().min(1).max(20_000).optional(),
    budgetCents: z.number().int().min(1).max(1_000_000).nullable().optional(),
    /**
     * The runner to use, by name (RUNNER_PINS): beats the project overlay's `execution.runner`.
     * Refused with `runner_unsupported` when that runner cannot serve the template or is not
     * installed; absent, the deployment's rule chooses and records why.
     */
    runner: z.enum(RUNNER_PINS).optional(),
    /** The project's memory scopes the session joins, over the overlay's `memory.scopes`; an unknown one is 422 memory_scope_unknown. */
    memoryScopes: z
        .array(z.string().regex(MEMORY_SCOPE_SLUG))
        .min(1)
        .max(MEMORY_SCOPES_PER_SESSION_MAX)
        .optional(),
});
/**
 * The provider refused the run's start itself (its own 4xx other than 429: a key it does not
 * take, a workspace or a resource it does not know). A retry cannot change it until someone
 * changes the provider's side. The run is recorded as failed.
 */
export const PROVIDER_REJECTED = "provider_rejected";
/**
 * The provider could not serve the run's start now: it could not be reached, it throttled the
 * call (429) or it failed (5xx). A retry later may succeed. The run is recorded as failed.
 */
export const ENGINE_UNAVAILABLE = "engine_unavailable";
export const runs = {
    start: oc
        .route({
        summary: "Start a run",
        description: "Composes a session from the project's current revision (the checker gates it: blocked is 422), sends the outcome and the mission, and returns the run. The budget is the session's cap in cents; omitted means the server's default cap ($5), and more than the server's maximum ($20) is 422 run_budget_too_high. An account that started its plan's daily run cap of runs in the rolling 24 hours ending now (every status, replays included) is 422 daily_run_cap_reached {cap, used, resetsAt}, after what the documents refuse and before the budget; a plan with no cap is unlimited. An overlay that is not YAML is 422 overlay_invalid. A provider that refuses the start itself (its own 4xx other than 429) is 502 provider_rejected with data {engine, providerStatus, providerMessage, providerRequestId when the provider named one}; a provider that cannot be reached, throttles the call or fails is 503 engine_unavailable with data {engine, retryAfterSeconds when the provider named a wait}; either way the run is recorded as failed. With baseRevision, a project whose current revision is another (a save landed since the caller read it) is 409 draft_conflict {currentRevision}, as projects.saveDraft answers, and nothing starts. With an Idempotency-Key header, a retry of the same start answers the same run (Idempotency-Replayed: true) and starts nothing; the same key with another start is 409 idempotency_key_reused, whose data.runId is the run the key started.",
    })
        .meta({ access: "write" })
        .input(RunStartInputSchema)
        .output(z.object({ run: RunSummarySchema })),
    preflight: oc
        .route({
        summary: "Preflight a run",
        description: "What starting this run would check, for the signed-in owner of the project, without starting it: nothing is written, nothing is spent and no provider is called. Answers the project's current revision with the start's blockers in order (the first is the error start would answer), the checker's verdict and findings for the engine the model line selects, whether that engine is installed on this deployment, what the run needs (web, running code, connectors, skills, MCP hosts) and the cap's default and maximum in cents. Known blockers are a normal answer (ready: false); a project you cannot see is 404.",
    })
        .meta({ access: "read" })
        .input(RunStartInputSchema)
        .output(RunPreflightSchema),
    replay: oc
        .route({
        summary: "Replay a finished run",
        description: "Starts a NEW run on the finished run's project whose conversation is seeded from that run's kept transcript, then sends the message as its first turn. Loop runs only (422 replay_unsupported otherwise). Every run, a loop run included, takes its follow-up as a message command on its own session; a replay is a separate run that keeps none of the source's threads. A run that kept no transcript is 422 replay_no_transcript (a Foundry run's conversations are captured when its session ends, so one still taking follow-ups has none yet); a run still going is 409 run_not_finished. The budget and the plan's daily run cap work as in start (a replay is a start: it counts and is refused the same), and so does an Idempotency-Key: a retry of the same replay answers the same run, and the same key with another replay is 409 idempotency_key_reused naming that run (data.runId).",
    })
        .meta({ access: "write" })
        .input(z.object({
        runId: z.string().uuid(),
        message: z.string().min(1).max(20_000),
        budgetCents: z.number().int().min(1).max(1_000_000).nullable().optional(),
    }))
        .output(z.object({ run: RunSummarySchema })),
    get: oc
        .route({
        summary: "One run",
        description: "The run's detail: status, usage, outcome verdicts, capabilities, the credential that started it (startedBy: the cookie, a bearer session, or an API key with its id and name), and lastSequence, the sequence of the log's last event, read after the row: a reader holding every event up to it has nothing of the log left to read as of that row.",
    })
        .meta({ access: "read" })
        .input(z.object({ runId: z.string().uuid() }))
        .output(z.object({ run: RunDetailSchema })),
    listForProject: oc
        .route({ summary: "A project's runs", description: "Newest first." })
        .meta({ access: "read" })
        .input(z.object({ projectId: z.string().uuid() }))
        .output(z.object({ runs: z.array(RunSummarySchema) })),
    history: oc
        .route({
        summary: "The viewer's run history",
        description: "Across projects, newest first (by createdAt, then id); each row names the credential that started the run (startedBy), as runs.get does. Pages are at most `limit` runs (default 50); pass `nextCursor` back as `cursor` for the next, older page, and null means there is none. Every page holds only the viewer's runs, and each run is on one page: runs that share a createdAt are neither skipped nor repeated, and a run started after the first page was read is on no later page (it is on a new first page). A cursor runs.history did not issue is 400 invalid_cursor.",
    })
        .meta({ access: "read" })
        .input(z.object({
        limit: z.number().int().min(1).max(100).optional(),
        cursor: z.string().min(1).max(500).optional(),
    }))
        .output(z.object({ runs: z.array(HistoryRunSchema), nextCursor: z.string().nullable() })),
    command: oc
        .route({
        summary: "Send a command to a run",
        description: "A message, an interrupt, a thread archive, a tool decision or a custom tool result, delivered to the session as the matching user event. Accepted means logged; the session answers through the event stream. An interrupt with a threadId stops that child thread only (the Session threads page's session_thread_id), and archive_thread archives one idle child thread (requires_action counts), freeing its place under the 25-child cap: a thread the run never created is 404 thread_not_found, a terminated one is 400 thread_terminated, archiving a running one is 400 thread_running, and either command on a workflow run's thread while its run is open is 400 workflow_run_open. A message to a finished run is its next turn on the same session on every runner (the run reads running again, its log continues, and a loop run's subagent threads keep their histories); a run whose turn failed `retries_exhausted` takes one too. Once that run failed otherwise, its last turn ended longer ago than the follow-up window, or a newer run on the project superseded it, the message is 409 run_not_resumable with the reason. A message that reopens a finished run an account key started counts as one of that key's open runs, whoever sends it: while the key holds five it is 429 key_open_runs_exceeded {max, open}, as a sixth start is. A tool decision or custom tool result for a call whose thread terminated has no effect: it is 409 command_target_closed with the thread's id, as is one the provider took only after the thread ended. The command's commandId is its Idempotency-Key when no header is sent: the same command sent twice is delivered once, and the second answer is a replay. The user event a command becomes names its commandId on its envelope (`commandId`), so a client matches the row it echoed to the stored one exactly.",
    })
        .meta({ access: "write" })
        .input(z.object({ runId: z.string().uuid(), command: RunCommandSchema }))
        .output(z.object({ accepted: z.literal(true) })),
    threads: oc
        .route({
        summary: "A run's threads",
        description: "The live session's threads as the Session threads page lists them: the primary (parentThreadId null) first, then every child, archived ones included (terminated). A workflow run's threads carry its workflowRunId. statuses keeps only the threads in one of them. A run whose session is not live is 409 run_not_live; the run log's thread rows stay readable through events.",
    })
        .meta({ access: "read" })
        .input(z.object({
        runId: z.string().uuid(),
        statuses: z.array(RunThreadStatusSchema).min(1).max(4).optional(),
    }))
        .output(z.object({ threads: z.array(RunThreadSchema) })),
    events: oc
        .route({
        summary: "A run's events",
        description: "The durable log after a sequence number, projected to the console's event vocabulary; the stream endpoint tails the same log.",
    })
        .meta({ access: "read" })
        .input(z.object({
        runId: z.string().uuid(),
        afterSequence: z.number().int().min(-1).optional(),
        limit: z.number().int().min(1).max(500).optional(),
    }))
        .output(z.object({
        events: z.array(RunEventEnvelopeSchema),
        nextSequence: z.number().int().nullable(),
    })),
    view: oc
        .route({
        summary: "A run's view",
        description: "The run's SessionView, live or finished: what a front-end renders (status, mode, what the run needs from the person, its agents, workflow runs, open asks, queued messages, usage, outputs), folded on the server from the run's log with the contracts fold (@mfa/v5-contracts/run-view) and with runs.get's detail folded in last (foldRunRow). throughSequence is the last log sequence in it: follow on with the stream after it (?after=). A queued message's processed copy takes it out of queued, although the stream sends no second envelope for it. The stream's ?view=1 interleaves the same view as frames.",
    })
        .meta({ access: "read" })
        .input(z.object({ runId: z.string().uuid() }))
        .output(z.object({
        view: SessionViewSchema,
        throughSequence: z.number().int().min(-1),
    })),
    setBudget: oc
        .route({
        summary: "Change a run's budget",
        description: "Raise or lower the session's cap in cents (it must exceed what was consumed); a request held at the cap goes out when the cap allows. No request removes the cap: null is 422 run_budget_required, and more than the server's maximum ($20) is 422 run_budget_too_high {maxCents}, as in start.",
    })
        .meta({ access: "write" })
        .input(z.object({
        runId: z.string().uuid(),
        cents: z
            .number()
            .int()
            .min(1)
            .nullable()
            .describe("The new cap in cents. null is refused (422 run_budget_required): no request removes a run's cap."),
    }))
        .output(z.object({ ok: z.boolean(), reason: z.string().optional() })),
    outputs: oc
        .route({
        summary: "A run's deliverables",
        description: "What the agent wrote under /mnt/session/outputs: text files, and from a sandbox binaries too. The bytes of either come from GET /runs/:runId/outputs/content?path=&mode=preview|download.",
    })
        .meta({ access: "read" })
        .input(z.object({ runId: z.string().uuid() }))
        .output(z.object({
        outputs: z.array(z.object({
            path: z.string(),
            sizeBytes: z.number().int().nonnegative(),
            mediaType: z.string().min(1),
        })),
    })),
    output: oc
        .route({
        summary: "One deliverable's text",
        description: "The file's content as text; a binary deliverable is 415 output_not_text (its bytes come from the content route).",
    })
        .meta({ access: "read" })
        .input(z.object({ runId: z.string().uuid(), path: z.string().min(1).max(1_000) }))
        .output(z.object({ path: z.string(), content: z.string() })),
};
//# sourceMappingURL=runs.js.map